<template>
    <div :class="classes">
        <label :class="labelClasses" @click="onChange(!modelValue)">
            <slot />
        </label>

        <Switch
            data-testid="labeled-switch__control"
            :loading="loading"
            :size="size"
            :model-value="modelValue"
            @onChange="onChange"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

export default defineComponent({
    name: "LabeledSwitch",
    props: {
        loading: {
            type: Boolean
        },
        size: {
            type: String as PropType<"small" | "large">,
            validator(value: string) {
                return ["small", "large"].includes(value);
            }
        },
        type: {
            type: String as PropType<"default" | "success" | "info">,
            validator(value: string) {
                return ["default", "success", "info"].includes(value);
            }
        },
        modelValue: {
            required: true,
            type: Boolean
        }
    },
    emits: ["change", "update:modelValue"],
    computed: {
        classes() {
            const classes = ["labeled-switch"];

            if (this.type) {
                classes.push(`labeled-switch--${this.type}`);
            }

            return classes;
        },
        labelClasses() {
            const classes = ["labeled-switch__label"];
            if (!this.modelValue) {
                classes.push("labeled-switch__label--off");
            }
            return classes;
        }
    },
    methods: {
        onChange(value: boolean) {
            if (!this.loading) {
                this.$emit("change", value);
                this.$emit("update:modelValue", value);
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.labeled-switch {
    align-items: center;
    display: inline-flex;
    flex: 1 0;
}

.labeled-switch--success {
    align-items: center;
    display: inline-flex;
    flex: 1 0;

    .ivu-switch-checked {
        background: $success-color;
        border-color: $success-color;

        &:focus {
            box-shadow: 0 0 0 2px rgba($success-color, 0.2);
        }
    }
}

.labeled-switch--info {
    align-items: center;
    display: inline-flex;
    flex: 1 0;

    .ivu-switch-checked {
        background: $info-color;
        border-color: $info-color;

        &:focus {
            box-shadow: 0 0 0 2px rgba($info-color, 0.2);
        }
    }
}

.labeled-switch__label {
    cursor: pointer;
    padding: 0 $spacing-smaller 0 0;
    transition: color 0.2s linear;
}

.labeled-switch__label--off {
    color: $grey5;
}
</style>
